/**
 * Smooth, paint-synced reveal of streamed text.
 *
 * Network tokens arrive in bursts, so feeding them straight to the UI looks
 * chunky. This drip-feeds the accumulated text via `requestAnimationFrame` at
 * a steady, frame-rate-independent cadence — speeding up when a lot is buffered
 * (so it never lags far behind) but capped so a large buffer never dumps at
 * once. The result reads like natural typing.
 */

export interface TypewriterOptions {
  /** Steady floor reveal speed (chars/sec) when nearly caught up. */
  baseCharsPerSecond: number
  /** Extra chars/sec added per buffered char, so bursts catch up gracefully. */
  catchUpCharsPerBufferedChar: number
  /** Ceiling on reveal speed so a large buffer never dumps in one frame. */
  maxCharsPerSecond: number
  /** Clamp on a single frame's elapsed time (guards tab-switch / GC gaps). */
  maxFrameSeconds: number
}

export const DEFAULT_TYPEWRITER_OPTIONS: TypewriterOptions = {
  baseCharsPerSecond: 75,
  catchUpCharsPerBufferedChar: 1.2,
  maxCharsPerSecond: 280,
  maxFrameSeconds: 0.05,
}

export interface TypewriterCallbacks {
  /** Called each frame with the text that should currently be visible. */
  onReveal: (visibleText: string) => void
  /** Called once the reveal has caught up after `finish()`. */
  onComplete: (finalText: string) => void
  /**
   * Called when the reveal has caught up to all received text and stayed idle
   * for `IDLE_SETTLE_MS` while the stream is still open (no `finish()` yet).
   * Lets the UI render the "settled" text (e.g. full markdown) during long
   * gaps — such as a tool call or the server computing follow-up actions —
   * instead of waiting for the terminal `done` event.
   */
  onIdle?: () => void
}

/** How long the reveal must stay caught-up before it counts as "settled". */
const IDLE_SETTLE_MS = 450

export interface Typewriter {
  /** Append newly streamed text to the reveal buffer. */
  append: (chunk: string) => void
  /** Ensure the full target is at least this text (e.g. a final payload). */
  setTarget: (fullText: string) => void
  /** Mark the stream finished; completes once the reveal catches up. */
  finish: () => void
  /** Stop immediately without completing (cancel / error). */
  stop: () => void
}

export const createTypewriter = (
  { onReveal, onComplete, onIdle }: TypewriterCallbacks,
  options: TypewriterOptions = DEFAULT_TYPEWRITER_OPTIONS
): Typewriter => {
  let target = ''
  let revealedLength = 0
  let finished = false
  let cancelled = false
  let rafId: number | null = null
  let lastFrameTs = 0
  let idleTimer: ReturnType<typeof setTimeout> | null = null

  const clearIdle = () => {
    if (idleTimer !== null) {
      clearTimeout(idleTimer)
      idleTimer = null
    }
  }

  // Once caught up with the stream still open, wait a beat and (if no new text
  // arrived) tell the consumer the text has settled.
  const scheduleIdle = () => {
    if (!onIdle || cancelled || idleTimer !== null) return
    idleTimer = setTimeout(() => {
      idleTimer = null
      if (!cancelled) onIdle()
    }, IDLE_SETTLE_MS)
  }

  // Pause the rAF but keep state — used internally when caught up and waiting
  // for more tokens; the loop re-arms on the next append/setTarget/finish.
  const pause = () => {
    if (rafId !== null) {
      cancelAnimationFrame(rafId)
      rafId = null
    }
    lastFrameTs = 0
  }

  // Terminal stop — after this the typewriter is dead and cannot be re-armed,
  // so a superseded stream can't resurrect a finalized message.
  const stop = () => {
    cancelled = true
    clearIdle()
    pause()
  }

  const charsForFrame = (bufferedChars: number, elapsedSeconds: number) => {
    const speed = Math.min(
      options.maxCharsPerSecond,
      options.baseCharsPerSecond +
        bufferedChars * options.catchUpCharsPerBufferedChar
    )
    return Math.max(1, Math.round(speed * elapsedSeconds))
  }

  const frame = (ts: number) => {
    if (cancelled) return
    if (!lastFrameTs) lastFrameTs = ts
    const elapsed = Math.min(options.maxFrameSeconds, (ts - lastFrameTs) / 1000)
    lastFrameTs = ts

    const buffered = target.length - revealedLength
    if (buffered > 0) {
      revealedLength = Math.min(
        target.length,
        revealedLength + charsForFrame(buffered, elapsed)
      )
      onReveal(target.slice(0, revealedLength))
      rafId = requestAnimationFrame(frame)
    } else if (finished) {
      pause()
      onComplete(target)
    } else {
      // Caught up while the stream is still open — idle until more arrives,
      // and after a short settle window let the consumer render the full text.
      pause()
      scheduleIdle()
    }
  }

  const ensureRunning = () => {
    if (cancelled || rafId !== null) return
    lastFrameTs = 0
    rafId = requestAnimationFrame(frame)
  }

  return {
    append: chunk => {
      if (cancelled || !chunk) return
      clearIdle()
      target += chunk
      ensureRunning()
    },
    setTarget: fullText => {
      if (cancelled) return
      clearIdle()
      if (fullText.length > target.length) target = fullText
      ensureRunning()
    },
    finish: () => {
      if (cancelled) return
      clearIdle()
      finished = true
      ensureRunning()
    },
    stop,
  }
}
